import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  LayoutDashboard, Ticket, PlusCircle, LogOut, BarChart3, Settings,
  FileText, Sun, Moon, Menu, X, ShieldCheck, User, Users, Database, Search,
  Bell,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import LogoBSE from "@/assets/logo/favicon.png";

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

const customerNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/tickets", label: "My Tickets", icon: Ticket },
  { to: "/tickets/new", label: "New Ticket", icon: PlusCircle },
];

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/tickets", label: "All Tickets", icon: Ticket },
  { to: "/admin/reports", label: "Reports", icon: FileText },
  { to: "/admin/settings", label: "SLA Settings", icon: Settings },
  { to: "/admin/master-data", label: "Master Data", icon: Database },
  { to: "/admin/staging", label: "Staging", icon: Database },
  { to: "/admin/master-data?view=search", label: "Search Serial Number", icon: Search },
];

const technicianNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/tickets", label: "Assigned Tickets", icon: Ticket },
  { to: "/admin/staging", label: "Staging", icon: Database },
  { to: "/admin/profile", label: "Profile", icon: User },
];

const superAdminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/tickets", label: "All Tickets", icon: Ticket },
  { to: "/admin/reports", label: "Reports", icon: FileText },
  { to: "/admin/settings", label: "SLA Settings", icon: Settings },
  { to: "/admin/master-data", label: "Master Data", icon: Database },
  { to: "/admin/staging", label: "Staging", icon: Database },
  { to: "/admin/master-data?view=search", label: "Search Serial Number", icon: Search },
  { to: "/admin/users", label: "Manage Users", icon: Users },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState([]);

  const isAdmin = ["admin", "super_admin", "technician"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";
  const nav = isSuperAdmin ? superAdminNav : user?.role === "technician" ? technicianNav : isAdmin ? adminNav : customerNav;

  const { data: notifications } = useQuery({
    queryKey: ["unread-tickets", user?.id],
    queryFn: () => api.get("/tickets/unread").then((r) => r.data),
    enabled: !!user,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (Array.isArray(notifications)) {
      setLocalNotifications(notifications);
    }
  }, [notifications]);

  const notificationList = Array.isArray(localNotifications) ? localNotifications : [];
  const unreadCount = notificationList.reduce((sum, n) => sum + (n.unread_count || 0), 0);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const openTicket = async (item) => {
    if (!item?.ticket_id) return;

    setLocalNotifications((prev) => prev.filter((entry) => entry.ticket_id !== item.ticket_id));

    try {
      await api.post(`/tickets/${item.ticket_id}/read`);
    } catch (e) {
      // no-op: still navigate user to ticket
    }

    const target = item.url || (isAdmin ? `/admin/tickets/${item.ticket_id}` : `/tickets/${item.ticket_id}`);
    navigate(target);
    setNotificationsOpen(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 lg:h-screen z-40 inset-y-0 left-0 w-64 glass-strong border-r border-border flex flex-col transition-transform duration-200 rounded-none ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border">
          <img src={LogoBSE} alt="BSE Logo" className="h-8 w-8 rounded-md object-cover" />
          <div className="leading-none">
            <div className="font-heading font-extrabold tracking-tight text-lg">Anyideas</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">Support Desk</div>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1">
          {nav.map(({ to, label, icon: Icon }) => {
            const hasQuery = to.includes("?");
            const active = hasQuery
              ? location.pathname === to.split("?")[0] && location.search === `?${to.split("?")[1]}`
              : !location.search && (location.pathname === to || (to !== "/dashboard" && to !== "/admin" && location.pathname.startsWith(to)));
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setOpen(false)} className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <Icon className="h-4 w-4" />{label}
              </Link>
            );
          })}
        </nav>

        <div className="flex-shrink-0 p-3 border-t border-border">
          {isAdmin && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-md bg-accent text-accent-foreground text-xs font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> {isSuperAdmin ? "Super Admin" : "Admin"} Access
            </div>
          )}
          <div className="px-3 py-2 text-sm">
            <div className="font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Link to="/profile" className="w-full">
            <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground" data-testid="profile-btn">
              <User className="h-4 w-4" /> Edit profile
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground" data-testid="logout-btn" onClick={doLogout}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-card/40 backdrop-blur-2xl border-b border-border flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="hidden lg:block font-heading font-bold tracking-tight text-lg">
            {isAdmin ? (user?.role === "technician" ? "Technician Workspace" : "Administration") : "Customer Portal"}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setNotificationsOpen((v) => !v)}
                className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-foreground hover:bg-secondary"
                aria-label="Notifications"
                data-testid="notification-bell"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <div className="absolute right-0 top-12 z-50 w-80 rounded-md border border-border bg-popover p-2 shadow-lg">
                  <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unread messages</div>
                  {notificationList.length === 0 ? (
                    <div className="px-2 py-4 text-sm text-muted-foreground">No unread messages.</div>
                  ) : (
                    <div className="max-h-80 space-y-2 overflow-y-auto">
                      {notificationList.map((item) => (
                        <button
                          key={item.ticket_id}
                          type="button"
                          onClick={() => openTicket(item)}
                          className="w-full rounded-md border border-border bg-card px-3 py-2 text-left transition hover:bg-secondary"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">{item.ticket_number}</span>
                            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
                              {item.unread_count} new
                            </span>
                          </div>
                          <div className="mt-1 truncate text-xs text-muted-foreground">{item.subject}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full mx-auto animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
